import { ApiResponse } from "types";
import { Apis, Helpers, Store } from "utils";
import { Enums } from "utils";

type AuthValue = {
  code?: string;
  uuid?: string;
};

type PassValue = {
  password?: string;
  wqp?: string;
};

export interface IAuthService {
  signup(data: { firstName: string; lastName: string; email: string; password: string; dob: string }): Promise<any>;
  findCodeExpiry(id: string): Promise<any>;
  requestCode(email: string, passValue: PassValue, authMethod: Enums.EnumAuthMethod): Promise<any>;
  signin(email: string, authValue: AuthValue): Promise<any>;
  signout(wq: string): Promise<any>;
  accountVerification(data: { email: string; code: string }): Promise<any>;
  resendVerificationEmail(email: string): Promise<any>;
  forgotPassword(email: string): Promise<any>;
  resetPassword(id: string, password: string): Promise<any>;
  resetPasswordAfterLogin(currentPassword: string, newPassword: string): Promise<any>;
  resetPasswordByAdmin(patientId: number, Password: string): Promise<any>;
}

export class AuthService implements IAuthService {
  private qubehub: any;

  constructor(accessToken: string) {
    this.qubehub = Apis.qubehub(accessToken);
  }

  /**
   * function to register a new patient
   * @param {object} data e.g. { firstName, lastName, email, password, dob }
   * @return
   */

  async signup(data: { firstName: string; lastName: string; email: string; password: string; dob: string }) {
    const { firstName, lastName } = data;
    const username = `${firstName},${lastName}`;
    return this.#handleResponse(await this.qubehub.post("patient/register", { username, email: data.email, password: data.password, dob: data.dob }));
  }

  async findCodeExpiry(id: string): Promise<any> {
    return this.#handleResponse(await this.qubehub.get(Helpers.interpolateURL("expiry/:id", { id })));
  }

  async requestCode(email: string, passValue: PassValue, authMethod: Enums.EnumAuthMethod) {
    return this.#handleResponse(await this.qubehub.post(Helpers.interpolateURL("request/code/:authMethod", { authMethod }), { email, ...passValue }));
  }

  async signin(email: string, authValue: AuthValue) {
    return this.#handleResponse(await this.qubehub.post("login", { email, ...authValue }));
  }

  /**
   * function to logout the user
   * @param {string} wq access token
   * @return
   */

  async signout(wq: string) {
    return this.#handleResponse(await this.qubehub.post("logout", { wq }));
  }

  /**
   * function to verify the user's account
   * @param {object} data e.g. { email, code }
   * @return
   */

  async accountVerification(data: { email: string; code: string }) {
    return this.#handleResponse(await this.qubehub.post("register/verify", data));
  }

  /**
   * function to resend the account verification email
   * @param {string} email email
   * @return
   */

  async resendVerificationEmail(email: string) {
    return this.#handleResponse(await this.qubehub.post("register/resend", { email }));
  }

  /**
   * function to request a password reset
   * @param {string} email email
   * @return
   */

  async forgotPassword(email: string) {
    return this.#handleResponse(await this.qubehub.post("reset/pass/email", { email }));
  }

  /**
   * function to update user's password
   * @param {string} id user id
   * @param {string} password new password
   * @return
   */

  async resetPassword(id: string, password: string) {
    return this.#handleResponse(await this.qubehub.post(Helpers.interpolateURL("reset/pass/:id", { id }), { password }));
  }

  /**
   * function to update user's password after login
   * @param {string} currentPassword new password
   * @param {string} newPassword new password
   * @return
   */

  async resetPasswordAfterLogin(currentPassword: string, newPassword: string) {
    return this.#handleResponse(await this.qubehub.post("reset/pass", { currentPassword, newPassword }));
  }

  /**
   * function to update patient's password by clinic admin
   * @param {number} patientId new password
   * @param {string} password new password
   * @return
   */

  async resetPasswordByAdmin(patientId: number, password: string) {
    return this.#handleResponse(await this.qubehub.post("reset/pass/admin", { patientId, password }));
  }

  /**
   * method to handle the response from the API
   * @param {any} response the data retrieved from the API
   * @returns
   */

  #handleResponse(response: any) {
    try {
      if (response && response.data) {
        const { status = Enums.EnumResponse.Success, payload = null }: any = response.data;
        if (status === Enums.EnumResponse.Unauthorized) Store.sessionStorage.clear();
        // TODO: this should be simplified
        const { authorization } = response.headers;
        return payload ? [status, authorization ? { ...payload, wq: authorization.split(" ")[1] } : payload] : [status, null];
      }
    } catch (err: any) {
      return [Enums.EnumResponse.Error, null];
    }
    return [Enums.EnumResponse.None, null];
  }
}
